import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {
  buildFunctionStartAtom,
  getParameters,
  inferFunctionStartType,
  TransformerIdsEnum,
} from '../../../editors/expressions/expressionInferHelpers';
import {RuleMultilineParameterCard} from '../ruleMultilineParameterCard';
import type {AtomCardProps} from './atomCard';
import {AtomIcon} from './atomIcon';
import {AtomsList} from './atomsList';
import {AtomTitle} from './atomTitle';
import {AtomParameterPicker} from './pickers/atomParameterPicker';

/*
 * Styles.
 */

const StyledRuleFunction = styled.div`
  width: 100%;
  display: grid;
  grid-auto-rows: min-content;
  grid-row-gap: 5px;
  justify-items: center;

  padding: 8px;
  background-color: ${(p) => p.theme.greys.shade10};
  border-radius: 16px;
`;

/*
 * Component.
 */

export const FunctionAtomCard: FC<AtomCardProps> = (props) => {
  const {atom, isDisabled, onCancel} = props;
  const {functionAtoms} = atom;
  const title = renderFirstLine(props);

  const functionAtomsWithStart = useMemo(() => {
    const functionStartType = inferFunctionStartType(atom);
    const functionStart = buildFunctionStartAtom(functionStartType);
    return [functionStart, ...(functionAtoms || [])];
  }, [atom, functionAtoms]);

  return (
    <StyledRuleFunction>
      <RuleMultilineParameterCard title={title} icon={<AtomIcon atom={atom} />} onCancel={onCancel} />
      <AtomsList
        atoms={functionAtomsWithStart}
        expressionDependencies={props.expressionDependencies}
        isReadOnly={isDisabled}
        onUpdateAtom={props.onUpdateAtom}
        onAddAtom={props.onAddAtom}
        onDeleteAtom={props.onDeleteAtom}
        isNested
      />
    </StyledRuleFunction>
  );
};

/*
 * Helpers.
 */

function renderFirstLine(props: AtomCardProps) {
  const {atom, isDisabled, onUpdateAtom} = props;

  if (atom.transform !== TransformerIdsEnum.SORT_BY) {
    return <AtomTitle atom={atom} />;
  }

  const [directionParam] = getParameters(atom);

  return (
    <AtomTitle atom={atom}>
      <AtomParameterPicker
        parameter={directionParam}
        atom={atom}
        isDisabled={isDisabled}
        onUpdate={onUpdateAtom}
      />
    </AtomTitle>
  );
}
